import {
  DefaultError,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import { useTranslation } from 'react-i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import { ITrackFingerprintLookup, ITrackFingerprintSubmission } from './types';

export const useGetTrackFingerprintLookup = (id: string, enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ITrackFingerprintLookup, DefaultError>({
    queryKey: ['track-fingerprint-lookup', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Track/Fingerprint/Lookup`,
        params: { trackId: id },
      });

      return responseHandler(response, {
        groupName: `useGetTrackFingerprintLookup - ${id}`,
        notFoundPath: '/track',
      });
    },
    enabled: enabled,
    retry: false,
  });
};

export const usePostTrackFingerprintSubmission = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostTrackFingerprintSubmission'
  );

  return useMutation<unknown, DefaultError, string>({
    mutationFn: async trackId => {
      loading(
        t('service-message.create.loading', {
          what: 'fingerprint',
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'Track/Fingerprint/Submit',
        body: { trackId },
      });

      return responseHandler(response, {
        groupName: `usePostTrackFingerprintSubmission`,
        notFoundPath: '/track',
      });
    },
    onSuccess: async (_data, trackId) => {
      await queryClient.invalidateQueries({ queryKey: ['track', trackId] });
      await queryClient.invalidateQueries({
        queryKey: ['track-fingerprint-submission', trackId],
      });
      success(
        t('service-message.create.success', {
          what: 'fingerprint',
        })
      );
    },
    onError: dismiss,
  });
};

export const useGetTrackFingerprintSubmissions = (
  id: string,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ITrackFingerprintSubmission[], DefaultError>({
    queryKey: ['track-fingerprint-submission', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Track/Fingerprint/Submissions`,
        params: { trackId: id },
      });

      return responseHandler(response, {
        groupName: `useGetTrackFingerprintSubmissions - ${id}`,
        onNotFound: () => {},
      });
    },
    enabled: enabled,
  });
};
